import React, { useState, useEffect } from "react";
import { Plus, Receipt, Pencil, Trash2 } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { expenses as api, type Expense, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

const MAX_RECEIPT_BYTES = 700_000;

function readReceiptAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const CATEGORIES = ["Maintenance", "Utilities", "Security", "Housekeeping", "Repairs", "Landscaping", "Admin", "Other"];

const INIT = { category: "Maintenance", vendor: "", amount: "", expenseDate: "", description: "" };

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ExpensesPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [receiptBase64, setReceiptBase64] = useState("");
  const [receiptPreview, setReceiptPreview] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmExpense, setConfirmExpense] = useState<Expense | null>(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const openAdd = () => {
    setEditing(null); setFormError(""); setForm(INIT);
    setReceiptBase64(""); setReceiptPreview(""); setShowForm(true);
  };
  const openEdit = (e: Expense) => {
    setEditing(e); setFormError("");
    setForm({
      category: e.category, vendor: e.vendor ?? "", amount: String(e.amount),
      expenseDate: e.expenseDate, description: e.description ?? "",
    });
    setReceiptBase64(e.receiptBase64 ?? "");
    setReceiptPreview(e.receiptBase64 ? `data:image/jpeg;base64,${e.receiptBase64}` : "");
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false); setEditing(null); setForm(INIT);
    setReceiptBase64(""); setReceiptPreview(""); setFormError("");
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_RECEIPT_BYTES) {
      setFormError("Receipt is too large — please use an image under 500KB.");
      return;
    }
    const b64 = await readReceiptAsBase64(file);
    setReceiptBase64(b64);
    setReceiptPreview(`data:${file.type};base64,${b64}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError("");
    try {
      const payload = {
        category: form.category, vendor: form.vendor || undefined,
        amount: Number(form.amount), expenseDate: form.expenseDate,
        description: form.description || undefined,
        receiptBase64: receiptBase64 || undefined,
      };
      if (editing) {
        const updated = await api.update(societyId, editing.id, payload);
        setItems(p => p.map(x => x.id === updated.id ? updated : x));
      } else {
        const created = await api.create(societyId, payload);
        setItems(p => [created, ...p]);
      }
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to save expense.");
    } finally { setSaving(false); }
  };

  const handleDelete = (e: Expense) => { setConfirmExpense(e); setDeleteError(""); };

  const confirmDelete = async () => {
    if (!societyId || !confirmExpense) return;
    setDeletingId(confirmExpense.id);
    try {
      await api.delete(societyId, confirmExpense.id);
      setItems(p => p.filter(x => x.id !== confirmExpense.id));
      setConfirmExpense(null);
    } catch { setDeleteError("Failed to delete expense."); }
    finally { setDeletingId(null); }
  };

  const total = items.reduce((sum, e) => sum + e.amount, 0);

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Expenses
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} expenses · {fmtAmount(total)} total
          </p>
        </div>
        <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Log Expense
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            {
              key: "receipt", header: "",
              render: (e: Expense) => e.receiptBase64
                ? <img src={`data:image/jpeg;base64,${e.receiptBase64}`} alt="Receipt" style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 6 }} />
                : null,
            },
            { key: "category", header: "Category", cellStyle: { fontWeight: 500 }, render: (e: Expense) => e.category },
            { key: "vendor", header: "Vendor", cellStyle: { color: "var(--nx-text-secondary)" }, render: (e: Expense) => e.vendor || "—" },
            { key: "amount", header: "Amount", cellStyle: { fontWeight: 600 }, render: (e: Expense) => fmtAmount(e.amount) },
            { key: "date", header: "Date", cellStyle: { color: "var(--nx-text-muted)" }, render: (e: Expense) => e.expenseDate },
            { key: "description", header: "Description", cellStyle: { color: "var(--nx-text-muted)" }, render: (e: Expense) => e.description || "—" },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (e: Expense) => (
                <>
                  <button onClick={() => openEdit(e)} style={actionBtn} title="Edit expense"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(e)} disabled={deletingId === e.id} style={{ ...actionBtn, color: "#EF4444", opacity: deletingId === e.id ? 0.4 : 1 }} title="Delete expense">
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={items}
          rowKey={e => e.id}
          loading={loading}
          emptyIcon={<Receipt size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No expenses logged yet."
        />
      </div>

      <ConfirmDialog
        open={confirmExpense !== null}
        variant="danger"
        title={`Delete "${confirmExpense?.category}" expense?`}
        message="This expense record will be permanently removed."
        confirmLabel="Delete Expense"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmExpense(null); setDeleteError(""); }}
      />

      {showForm && (
        <Modal
          title={editing ? "Edit Expense" : "Log Expense"}
          submitLabel={editing ? "Save Changes" : "Log Expense"}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Category *</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={inp}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Vendor</label>
              <input value={form.vendor} onChange={e => setForm(p => ({ ...p, vendor: e.target.value }))} placeholder="ABC Services" style={inp} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Amount (₹) *</label>
              <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} required min="0" step="0.01" placeholder="5000" style={inp} />
            </div>
            <div>
              <label style={lbl}>Date *</label>
              <input type="date" value={form.expenseDate} onChange={e => setForm(p => ({ ...p, expenseDate: e.target.value }))} required style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Description</label>
            <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional notes" style={inp} />
          </div>
          <div>
            <label style={lbl}>Receipt (optional)</label>
            <input type="file" accept="image/*" onChange={handlePhotoChange} style={inp} />
            {receiptPreview && (
              <img src={receiptPreview} alt="Receipt preview" style={{ marginTop: 8, width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid var(--nx-border)" }} />
            )}
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default ExpensesPage;
